import type { ComponentProps } from 'react'
import { lazy, Suspense } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

// Lazy load MuxPlayer to reduce initial bundle size
const MuxPlayer = lazy(() => import('@mux/mux-player-react'))

interface VideoProps extends ComponentProps<typeof MuxPlayer> {
  /**
   * Aspect ratio of the video (width / height).
   * Used to prevent layout shift while loading.
   * Default: 16/9 (1.777)
   */
  aspectRatio?: number
}

/**
 * Video component that wraps MuxPlayer with lazy loading and layout stability.
 *
 * Features:
 * - Lazy loads MuxPlayer chunk
 * - Prevents layout shift via aspectRatio prop
 * - Shows skeleton loader while loading
 * - Applies cinematic styling on desktop (matches Image component)
 */
export function Video({
  className,
  aspectRatio = 16 / 9,
  style,
  ...props
}: VideoProps) {
  return (
    <div
      className={cn(
        'video-container my-8 overflow-hidden rounded-md bg-none',
        className,
      )}
      style={{
        aspectRatio,
        ...style,
      }}
    >
      <Suspense
        fallback={<Skeleton className="h-full w-full rounded-none bg-black" />}
      >
        <MuxPlayer className="h-full w-full object-fill" {...props} />
      </Suspense>
    </div>
  )
}
